import React, { useEffect, useRef, useState } from 'react';

/* global ApplePaySession */

const PAYPAL_SCRIPT_ID = 'paypal-sdk-script-v2';
const APPLE_PAY_SCRIPT_ID = 'apple-pay-sdk-script';

function loadPayPalSdk(clientId, paypalEnv = 'sandbox') {
  if (!clientId) {
    return Promise.reject(new Error('PayPal client ID is not configured'));
  }
  if (window.paypal?.Applepay) {
    return Promise.resolve(window.paypal);
  }
  const existing = document.getElementById(PAYPAL_SCRIPT_ID);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(window.paypal));
      existing.addEventListener('error', () => reject(new Error('Failed to load PayPal SDK')));
    });
  }

  const sdkHost = paypalEnv === 'live' ? 'www.paypal.com' : 'www.sandbox.paypal.com';

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = PAYPAL_SCRIPT_ID;
    script.src = `https://${sdkHost}/sdk/js?client-id=${encodeURIComponent(clientId)}&currency=USD&intent=capture&components=buttons,applepay`;
    script.async = true;
    script.onload = () => {
      if (window.paypal) resolve(window.paypal);
      else reject(new Error('PayPal SDK loaded but unavailable'));
    };
    script.onerror = () => reject(new Error('Failed to load PayPal SDK'));
    document.body.appendChild(script);
  });
}

function loadApplePaySdk() {
  if (window.ApplePaySession) {
    return Promise.resolve();
  }
  const existing = document.getElementById(APPLE_PAY_SCRIPT_ID);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Apple Pay SDK')));
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = APPLE_PAY_SCRIPT_ID;
    script.src = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Apple Pay SDK'));
    document.body.appendChild(script);
  });
}

async function createOrderOnServer({ apiBaseUrl, idToken, planId, billingTerm, appOrigin, orderId }) {
  const response = await fetch(`${apiBaseUrl}/api/payments/paypal/create-order`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      plan_id: planId,
      billing_term: billingTerm,
      app_origin: appOrigin,
      ...(orderId ? { order_id: orderId } : {}),
    }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload.error || 'Failed to create PayPal order');
  }
  return {
    orderId: payload.order_id,
    paypalOrderId: payload.paypal_order_id,
  };
}

async function captureOrderOnServer({ apiBaseUrl, idToken, orderId, paypalOrderId }) {
  const response = await fetch(`${apiBaseUrl}/api/payments/paypal/capture-order`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      order_id: orderId,
      paypal_order_id: paypalOrderId,
    }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.access_granted) {
    throw new Error(payload.error || 'Payment capture could not be confirmed. Please contact support.');
  }
  return payload;
}

export default function PayPalCheckoutButtons({
  apiBaseUrl,
  idToken,
  planId,
  billingTerm,
  appOrigin,
  amountUsd,
  orderId,
  onCaptureSuccess,
  onError,
  onApplePayEligibleChange,
}) {
  const applePayContainerRef = useRef(null);
  const buttonsContainerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [localError, setLocalError] = useState('');
  const [applePayEligible, setApplePayEligible] = useState(false);
  const orderRef = useRef({ orderId: null, paypalOrderId: null });

  useEffect(() => {
    let cancelled = false;
    let buttonsInstance = null;
    let applePayClickHandler = null;

    async function setupApplePay(paypal, applepayConfig) {
      if (!applePayContainerRef.current) return;
      if (!window.ApplePaySession || !ApplePaySession.canMakePayments()) return;
      if (!applepayConfig?.isEligible) return;

      const applepay = paypal.Applepay();
      applePayContainerRef.current.innerHTML =
        '<apple-pay-button id="cognidoor-apple-pay-btn" buttonstyle="black" type="buy" locale="en"></apple-pay-button>';

      const applePayButton = document.getElementById('cognidoor-apple-pay-btn');
      if (!applePayButton) return;

      applePayClickHandler = async () => {
        setProcessing(true);
        setLocalError('');
        try {
          const { orderId: createdOrderId, paypalOrderId } = await createOrderOnServer({
            apiBaseUrl,
            idToken,
            planId,
            billingTerm,
            appOrigin,
            orderId,
          });
          orderRef.current = { orderId: createdOrderId, paypalOrderId };

          const paymentRequest = {
            countryCode: applepayConfig.countryCode,
            merchantCapabilities: applepayConfig.merchantCapabilities,
            supportedNetworks: applepayConfig.supportedNetworks,
            currencyCode: 'USD',
            total: {
              label: 'Cognidoor',
              type: 'final',
              amount: Number(amountUsd).toFixed(2),
            },
          };

          const session = new ApplePaySession(4, paymentRequest);

          session.onvalidatemerchant = (event) => {
            applepay
              .validateMerchant({
                validationUrl: event.validationURL,
                displayName: 'Cognidoor',
              })
              .then((validateResult) => {
                session.completeMerchantValidation(validateResult.merchantSession);
              })
              .catch(() => {
                session.abort();
                setProcessing(false);
                setLocalError('Apple Pay merchant validation failed. Please try again.');
              });
          };

          session.onpaymentauthorized = async (event) => {
            try {
              const { orderId: currentOrderId, paypalOrderId: currentPaypalOrderId } = orderRef.current;
              if (!currentOrderId || !currentPaypalOrderId) {
                throw new Error('Checkout session expired. Please try again.');
              }

              await applepay.confirmOrder({
                orderId: currentPaypalOrderId,
                token: event.payment.token,
                billingContact: event.payment.billingContact,
              });

              session.completePayment(ApplePaySession.STATUS_SUCCESS);

              const payload = await captureOrderOnServer({
                apiBaseUrl,
                idToken,
                orderId: currentOrderId,
                paypalOrderId: currentPaypalOrderId,
              });

              if (onCaptureSuccess) {
                onCaptureSuccess({ orderId: currentOrderId, captureId: payload.capture_id });
              }
            } catch (err) {
              session.completePayment(ApplePaySession.STATUS_FAILURE);
              const message = err?.message || 'Apple Pay payment failed';
              setLocalError(message);
              if (onError) onError(err instanceof Error ? err : new Error(message));
            } finally {
              setProcessing(false);
            }
          };

          session.oncancel = () => {
            setProcessing(false);
            setLocalError('Payment cancelled. You can try again when ready.');
          };

          session.begin();
        } catch (err) {
          setProcessing(false);
          const message = err?.message || 'Apple Pay checkout failed';
          setLocalError(message);
          if (onError) onError(err instanceof Error ? err : new Error(message));
        }
      };

      applePayButton.addEventListener('click', applePayClickHandler);
      if (!cancelled) {
        setApplePayEligible(true);
        if (onApplePayEligibleChange) onApplePayEligibleChange(true);
      }
    }

    async function setup() {
      setLoading(true);
      setLocalError('');
      setApplePayEligible(false);
      if (onApplePayEligibleChange) onApplePayEligibleChange(false);

      try {
        const configRes = await fetch(`${apiBaseUrl}/api/payments/config`);
        const config = await configRes.json().catch(() => ({}));
        const clientId = config.paypal_client_id || process.env.REACT_APP_PAYPAL_CLIENT_ID;
        const paypalEnv = config.paypal_env || 'sandbox';
        if (!clientId) {
          throw new Error('PayPal checkout is not configured. Please contact support.');
        }

        const [paypal] = await Promise.all([
          loadPayPalSdk(clientId, paypalEnv),
          loadApplePaySdk().catch(() => null),
        ]);
        if (cancelled) return;

        if (buttonsInstance && typeof buttonsInstance.close === 'function') {
          buttonsInstance.close();
        }
        if (buttonsContainerRef.current) {
          buttonsContainerRef.current.innerHTML = '';
        }
        if (applePayContainerRef.current) {
          applePayContainerRef.current.innerHTML = '';
        }

        if (window.ApplePaySession && ApplePaySession.canMakePayments() && paypal.Applepay) {
          try {
            const applepay = paypal.Applepay();
            const applepayConfig = await applepay.config();
            if (!cancelled && applepayConfig?.isEligible) {
              await setupApplePay(paypal, applepayConfig);
            }
          } catch (_) {
            /* Apple Pay not available — fail closed */
          }
        }

        if (cancelled || !buttonsContainerRef.current) return;

        buttonsInstance = paypal.Buttons({
          style: {
            layout: 'vertical',
            color: 'gold',
            shape: 'rect',
            label: 'paypal',
          },
          createOrder: async () => {
            setProcessing(true);
            setLocalError('');
            const { orderId: createdOrderId, paypalOrderId } = await createOrderOnServer({
              apiBaseUrl,
              idToken,
              planId,
              billingTerm,
              appOrigin,
              orderId,
            });
            orderRef.current = { orderId: createdOrderId, paypalOrderId };
            return paypalOrderId;
          },
          onApprove: async () => {
            setProcessing(true);
            setLocalError('');
            try {
              const { orderId, paypalOrderId } = orderRef.current;
              if (!orderId || !paypalOrderId) {
                throw new Error('Checkout session expired. Please try again.');
              }
              const payload = await captureOrderOnServer({
                apiBaseUrl,
                idToken,
                orderId,
                paypalOrderId,
              });
              if (onCaptureSuccess) {
                onCaptureSuccess({ orderId, captureId: payload.capture_id });
              }
            } catch (err) {
              const message = err?.message || 'Payment capture failed';
              setLocalError(message);
              if (onError) onError(err instanceof Error ? err : new Error(message));
              throw err;
            } finally {
              setProcessing(false);
            }
          },
          onCancel: () => {
            setProcessing(false);
            setLocalError('Payment cancelled. You can try again when ready.');
          },
          onError: (err) => {
            setProcessing(false);
            const message = err?.message || 'PayPal checkout error';
            setLocalError(message);
            if (onError) onError(new Error(message));
          },
        });

        if (!buttonsInstance.isEligible || buttonsInstance.isEligible()) {
          await buttonsInstance.render(buttonsContainerRef.current);
        } else {
          throw new Error('PayPal checkout is unavailable for this browser or region.');
        }
      } catch (err) {
        if (!cancelled) {
          setLocalError(err.message || 'Unable to load PayPal checkout');
          if (onError) onError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
        setProcessing(false);
      }
    }

    setup();

    return () => {
      cancelled = true;
      if (buttonsInstance && typeof buttonsInstance.close === 'function') {
        buttonsInstance.close();
      }
      const applePayButton = document.getElementById('cognidoor-apple-pay-btn');
      if (applePayButton && applePayClickHandler) {
        applePayButton.removeEventListener('click', applePayClickHandler);
      }
    };
  }, [
    apiBaseUrl,
    idToken,
    planId,
    billingTerm,
    appOrigin,
    amountUsd,
    orderId,
    onCaptureSuccess,
    onError,
    onApplePayEligibleChange,
  ]);

  return (
    <div>
      {loading ? (
        <p className="text-sm text-gray-600 mb-3">Loading PayPal checkout…</p>
      ) : null}
      {processing ? (
        <p className="text-sm text-blue-700 mb-3" role="status" aria-live="polite">
          Confirming payment with our server…
        </p>
      ) : null}
      {localError ? (
        <div className="text-sm text-red-600 mb-3" role="alert">
          {localError}
        </div>
      ) : null}
      <div
        ref={applePayContainerRef}
        className={applePayEligible ? 'mb-3 min-h-[44px]' : 'hidden'}
        aria-hidden={!applePayEligible}
      />
      <div ref={buttonsContainerRef} className="min-h-[45px]" />
    </div>
  );
}
